import {
  MutationState,
  useMutation,
  useMutationState,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { useTranslations } from 'next-intl';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { claimReward } from '@wwe-portal/web/services/payment/api';
import { useProcessingDialog } from './processing-dialog';
import { wait } from '@wwe-portal/ui/lib/timer';
import { PaymentContext } from '@wwe-portal/web/providers/stores/payment-store';
import { MUTATION_KEY_CLAIM_REWARD } from '@wwe-portal/web/utils/constants/query-key';
import { useInvalidateOffer } from './use-invalidate-offer';
import { checkOfferValiditiy } from '@wwe-portal/web/services/client/offer';

type UseRewardClaimProps = {
  onClaimError?: (error?: unknown) => Promise<void>;
  onClaimSuccess?: (
    data: {
      offerData: OfferData;
      transactionId: string;
    },
    context: PaymentContext
  ) => void;
};

interface ClaimMutationData {
  data: { transactionId: string };
}

interface ClaimMutationVariable {
  offerData: OfferData;
  context: PaymentContext;
}

type ClaimMutationState = MutationState<
  ClaimMutationData,
  Error,
  ClaimMutationVariable
>;

export const useIsClaimingRewards = (offerId: string) => {
  const data = useMutationState<ClaimMutationState>({
    filters: { mutationKey: [MUTATION_KEY_CLAIM_REWARD], status: 'pending' },
  });

  return data.some(
    (mutation) => mutation.variables?.offerData.OffersID === offerId
  );
};

export const useClaimReward = ({
  onClaimError,
  onClaimSuccess,
}: UseRewardClaimProps) => {
  const handleError = useCallback(
    async (error: unknown) => {
      onClaimError?.(error);
      addDDError(error);
    },
    [onClaimError]
  );
  const t = useTranslations();
  const invalidate = useInvalidateOffer();
  const { showProcess, showSuccess } = useProcessingDialog({
    progressData: {
      title: t('reward_load'),
      description: '',
      duration: VERIFY_DURATION_MS,
    },
    successData: {
      title: t('reward_claim_successful'),
      description: '',
    },
  });

  const { mutate: startClaimReward, isPending } = useMutation<
    ClaimMutationData,
    Error,
    ClaimMutationVariable
  >({
    mutationKey: [MUTATION_KEY_CLAIM_REWARD],
    mutationFn: async (payload) => {
      const validationResult = await checkOfferValiditiy(
        payload.offerData.OffersID
      );
      if (!validationResult.data.data.success) {
        throw new Error(validationResult.data.data.message);
      }
      const response = await claimReward(payload.offerData.OffersID);
      return { data: response.data.data };
    },
    onSuccess: async (response, variables) => {
      const offerData = variables.offerData;
      const transactionId = response.data.transactionId;
      const context = variables.context;
      showProcess();
      onClaimSuccess?.(
        {
          offerData,
          transactionId,
        },
        context
      );

      await Promise.all([invalidate(), wait(VERIFY_DURATION_MS)]);
      showSuccess();
    },

    onError: handleError,
  });

  return {
    startClaimReward,
    isPending,
  };
};
